import React, { useContext, useEffect, useState } from 'react';
import CbmContext from '../../../Context/Cbm/cbmContext';
import AdminContext from '../../../Context/Admin/adminContext';
import axios from 'axios';
import { getBackendUrl } from '../../../config/apiConfig';
import { fetchSubAreasByDistrict } from '../../../Services/adminApiService';
import usePermissions from '../../../hooks/usePermissions';
import '../UserManagement/UserManagement.css';

const StoreManagement = () => {
  const cbmContext = useContext(CbmContext);
  const adminContext = useContext(AdminContext);
  const { stores, getStores } = cbmContext;
  const { deleteStore, getAllStores } = adminContext;

  // Use centralized permissions hook
  const {
    portalUser,
    isAdmin: hasAdminPermission,
    hasOfficeAccess: hasOfficePermission,
    isDM,
  } = usePermissions();

  const backendUrl = getBackendUrl();

  const [selectedStore, setSelectedStore] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [showDisabledStores, setShowDisabledStores] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [subAreas, setSubAreas] = useState([]);
  const [allStores, setAllStores] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    storeNum: '',
    name: '',
    banner: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    districtId: '',
    subAreaId: '',
    disabled: false,
  });

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/v1/admin/districts`
        );
        setDistricts(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error loading districts:', error);
        setDistricts([]);
      }
    };

    fetchDistricts();
  }, [backendUrl]);

  useEffect(() => {
    const districtId = formData.districtId;
    if (!districtId) {
      setSubAreas([]);
      setFormData((prev) => ({ ...prev, subAreaId: '' }));
      return;
    }

    let isActive = true;

    const loadSubAreas = async () => {
      try {
        const response = await fetchSubAreasByDistrict(Number(districtId));
        if (!isActive) return;
        setSubAreas(Array.isArray(response) ? response : []);
        setFormData((prev) => ({
          ...prev,
          subAreaId: prev.subAreaId
            ? String(
                (Array.isArray(response) ? response : []).some(
                  (subArea) => String(subArea.id) === String(prev.subAreaId)
                )
                  ? prev.subAreaId
                  : ''
              )
            : '',
        }));
      } catch (error) {
        if (isActive) {
          console.error('Error loading sub-areas:', error);
          setSubAreas([]);
        }
      }
    };

    loadSubAreas();

    return () => {
      isActive = false;
    };
  }, [formData.districtId]);

  useEffect(() => {
    const fetchStores = async () => {
      setIsLoading(true);
      try {
        if (hasAdminPermission || hasOfficePermission) {
          // Admin and Office users see all stores
          if (!allStores) {
            const storesData = await getAllStores();
            setAllStores(storesData);
          }
        } else if (isDM && portalUser?.district) {
          // DM users see only their district stores
          if (!stores || stores.length === 0) {
            getStores(portalUser.district);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchStores();
  }, [
    getAllStores,
    getStores,
    stores,
    allStores,
    portalUser,
    hasAdminPermission,
    hasOfficePermission,
    isDM,
  ]);

  const createStore = async () => {
    try {
      const payload = {
        ...formData,
        district: formData.districtId
          ? { id: Number(formData.districtId) }
          : null,
        subArea: formData.subAreaId ? { id: Number(formData.subAreaId) } : null,
      };
      delete payload.districtId;
      delete payload.subAreaId;
      await axios.post(`${backendUrl}/api/stores/v1/createStore`, payload);
      alert(`Store created successfully: ${formData.storeNum}`);
      resetForm();
      // Refresh store list based on authority
      if (hasAdminPermission || hasOfficePermission) {
        const storesData = await getAllStores();
        setAllStores(storesData);
      } else if (portalUser?.district) {
        getStores(portalUser.district);
      }
    } catch (err) {
      alert(
        `Failed to create store: ${err.response?.data?.message || err.message}`
      );
    }
  };

  const updateStore = async () => {
    try {
      const payload = {
        ...formData,
        district: formData.districtId
          ? { id: Number(formData.districtId) }
          : null,
        subArea: formData.subAreaId ? { id: Number(formData.subAreaId) } : null,
      };
      delete payload.districtId;
      delete payload.subAreaId;
      await axios.put(`${backendUrl}/api/stores/v1/updateStore`, payload);
      alert(`Store updated successfully: ${formData.storeNum}`);
      resetForm();
      // Refresh store list based on authority
      if (hasAdminPermission || hasOfficePermission) {
        const storesData = await getAllStores();
        setAllStores(storesData);
      } else if (portalUser?.district) {
        getStores(portalUser.district);
      }
    } catch (err) {
      alert(
        `Failed to update store: ${err.response?.data?.message || err.message}`
      );
    }
  };

  const editStore = (store) => {
    setFormData({
      id: store.id,
      storeNum: store.storeNum,
      name: store.name || '',
      banner: store.banner,
      address: store.address || '',
      city: store.city,
      state: store.state,
      zip: store.zip || '',
      districtId: store.district?.id ? String(store.district.id) : '',
      subAreaId: store.subArea?.id ? String(store.subArea.id) : '',
      disabled: !!store.disabled,
    });
    setSelectedStore(store);
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({
      storeNum: '',
      name: '',
      banner: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      districtId: '',
      subAreaId: '',
      disabled: false,
    });
    setSelectedStore(null);
    setIsEditing(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      updateStore();
    } else {
      createStore();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'districtId') {
      setFormData((prev) => ({
        ...prev,
        districtId: value,
        subAreaId: '',
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeleteStore = async (storeId, storeNum) => {
    if (
      !window.confirm(
        `Are you sure you want to delete store "${storeNum}"? This will disable the store.`
      )
    ) {
      return;
    }

    const result = await deleteStore(storeId);

    if (result.success) {
      alert('Store deleted successfully');
      // Clear form if the deleted store was being edited
      if (selectedStore?.id === storeId) {
        resetForm();
      }
      // Refresh store list based on authority
      if (hasAdminPermission || hasOfficePermission) {
        const storesData = await getAllStores();
        setAllStores(storesData);
      } else if (portalUser?.district) {
        getStores(portalUser.district);
      }
    } else {
      alert(`Failed to delete store: ${result.error}`);
    }
  };

  // Use allStores for Admin/Office, district stores for DM
  const storeList =
    hasAdminPermission || hasOfficePermission ? allStores : stores;

  const filteredStores =
    storeList?.filter((store) => {
      if (!showDisabledStores && store.disabled) {
        return false;
      }
      const searchLower = filterText.toLowerCase();
      return (
        store.storeNum?.toLowerCase().includes(searchLower) ||
        store.name?.toLowerCase().includes(searchLower) ||
        store.banner?.toLowerCase().includes(searchLower) ||
        store.city?.toLowerCase().includes(searchLower) ||
        store.state?.toLowerCase().includes(searchLower)
      );
    }) || [];

  return (
    <div className="user-management">
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading stores...</p>
        </div>
      )}
      <div className="management-header">
        <h1>Store Management</h1>
        <p className="management-subtitle">
          Manage store locations and information
        </p>
      </div>

      {/* Store Form Section */}
      <div className="section-card">
        <div className="section-header">
          <h2>{isEditing ? 'Edit Store' : 'Create New Store'}</h2>
          {isEditing && (
            <button className="btn-cancel" onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="storeNum" className="required">
                Store Number
              </label>
              <input
                type="text"
                id="storeNum"
                name="storeNum"
                value={formData.storeNum}
                onChange={handleInputChange}
                required
                placeholder="Enter store number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="name">Store Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter store name (optional)"
              />
              <small
                style={{ color: '#718096', fontSize: '12px', marginTop: '4px' }}
              >
                A friendly name to identify this store
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="banner" className="required">
                Banner/Brand
              </label>
              <input
                type="text"
                id="banner"
                name="banner"
                value={formData.banner}
                onChange={handleInputChange}
                required
                placeholder="Enter banner name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter street address (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city" className="required">
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                placeholder="Enter city"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state" className="required">
                State
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
                maxLength={2}
                placeholder="MN"
              />
            </div>

            <div className="form-group">
              <label htmlFor="zip">ZIP Code</label>
              <input
                type="text"
                id="zip"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                placeholder="Enter ZIP code (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="districtId" className="required">
                District
              </label>
              <select
                id="districtId"
                name="districtId"
                value={formData.districtId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select district</option>
                {districts.map((district) => (
                  <option key={district.id} value={district.id}>
                    {district.districtName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subAreaId">Sub-Area</label>
              <select
                id="subAreaId"
                name="subAreaId"
                value={formData.subAreaId}
                onChange={handleInputChange}
                disabled={!formData.districtId}
              >
                <option value="">None</option>
                {subAreas.map((subArea) => (
                  <option key={subArea.id} value={subArea.id}>
                    {subArea.name}
                    {subArea.district?.districtName
                      ? ` (${subArea.district.districtName})`
                      : ''}
                  </option>
                ))}
              </select>
              <small
                style={{ color: '#718096', fontSize: '12px', marginTop: '4px' }}
              >
                Leave blank if this store is not assigned to a sub-area.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="disabled">Disabled</label>
              <select
                id="disabled"
                name="disabled"
                value={formData.disabled ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    disabled: e.target.value === 'true',
                  }))
                }
              >
                <option value="false">Active</option>
                <option value="true">Disabled</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit">
              {isEditing ? 'Update Store' : 'Create Store'}
            </button>
            {isEditing && (
              <button
                type="button"
                className="btn-secondary"
                onClick={resetForm}
              >
                Clear Form
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Store List Section */}
      <div className="section-card">
        <div className="section-header">
          <h2>All Stores ({filteredStores.length})</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <input
                type="checkbox"
                checked={showDisabledStores}
                onChange={(e) => setShowDisabledStores(e.target.checked)}
              />
              Show disabled
            </label>
            <input
              type="text"
              placeholder="Filter stores..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Store #</th>
                <th>Store Name</th>
                <th>Banner</th>
                <th>Sub-Area</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>ZIP</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStores.map((store, index) => (
                <tr key={index}>
                  <td>
                    <span className="username-badge">{store.storeNum}</span>
                  </td>
                  <td>
                    {store.name || (
                      <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td>{store.banner}</td>
                  <td>{store.subArea?.name || '—'}</td>
                  <td>{store.address}</td>
                  <td>{store.city}</td>
                  <td>{store.state}</td>
                  <td>{store.zip}</td>
                  <td>
                    {store.disabled ? (
                      <span className="permission-badge dm">Disabled</span>
                    ) : (
                      <span className="permission-badge office">Active</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => editStore(store)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() =>
                          handleDeleteStore(store.id, store.storeNum)
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StoreManagement;
